import { useEffect, useState, type KeyboardEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { kb } from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";
import { useDuviCoreAdminToast } from "../../components/duvi-core-admin-kit";

export const DUVI_CORE_ADMIN_KB_CATEGORIES = [
  "Technical",
  "Business",
  "Support",
];

export function useDuviCoreAdminKnowledgeBaseEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useDuviCoreAdminToast();

  const { data: existing } = useDuviCoreAdminQuery(() => kb.get(id!), [id], {
    enabled: !!id,
  });

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [content, setContent] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [saved, setSaved] = useState(
    id ? "Last saved 2h ago" : "Not saved yet",
  );

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setCategory(existing.category);
      setTags([...existing.tags]);
      setContent(existing.content);
    }
  }, [existing]);

  const canPublish = Boolean(title.trim() && category && content.trim());

  const addTag = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim();
      if (!tags.includes(t)) setTags([...tags, t]);
      setTagInput("");
    }
  };
  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  const appendSnippet = (snippet: string) => setContent((b) => b + snippet);

  const handleSaveDraft = () => {
    setSaved("Saved just now");
    toast("success", "Draft saved", "Your changes are safe.");
  };

  const handlePublish = async () => {
    if (!canPublish) return;
    setIsPublishing(true);
    try {
      const payload = {
        title: title.trim(),
        content,
        category,
        tags,
      };
      if (id) {
        await kb.update(id, payload);
      } else {
        await kb.create(payload);
      }
      setSaved("Saved just now");
      toast(
        "success",
        "Article published",
        `“${title.trim()}” is now live in ${category}.`,
      );
      navigate("/knowledge-base");
    } catch {
      // errors surfaced via the API client / session handling
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    isEdit: !!id,
    title,
    setTitle,
    category,
    setCategory,
    tags,
    tagInput,
    setTagInput,
    addTag,
    removeTag,
    content,
    setContent,
    appendSnippet,
    canPublish,
    isPublishing,
    saved,
    handleSaveDraft,
    handlePublish,
  };
}
